// Palettes for the on-site Theme Picker (components/site/theme-picker.tsx).
// A per-browser PREVIEW tool: audition light paper colours, an accent/brand
// colour, and a font pairing against the real marketing pages before we
// commit the winners as the canonical `.idukki` theme in app/globals.css.
//
// Three independent axes so you get every combination:
//   1. BACKGROUNDS — the paper. Light only (no dark backgrounds by design).
//   2. ACCENTS     — the brand pop: CTA fill + emphasis text + kicker + links.
//   3. fonts       — reused from styleguide-fonts.ts (FONT_PAIRS).
//
// Legibility is handled in theme-picker.tsx: an accent is used as a FILL (with
// auto black/white text) AND, separately, as legible emphasis TEXT on light
// (a darkened `--accent-ink` derivation), so a pale accent like lime is never
// rendered as washed-out text.

export type Background = {
  id: string
  name: string
  note: string
  /** Paper / page background hex */
  bg: string
  /** Primary text + dark surfaces (navy keeps the brand ink legible) */
  ink: string
}

export type Accent = {
  id: string
  name: string
  /** The accent hex. Used as CTA fill + (darkened) emphasis text. */
  hex: string
}

const NAVY = '#0B1120'

export const BACKGROUNDS: Background[] = [
  { id: 'cream',       name: 'Cream',       note: 'The shipped brand paper. Warm, editorial.', bg: '#FAF6F0', ink: NAVY },
  { id: 'ivory',       name: 'Ivory',       note: 'Soft warm white. Cleaner than cream.',       bg: '#FCFAF4', ink: NAVY },
  { id: 'porcelain',   name: 'Porcelain',   note: 'Crisp near-white. Max contrast.',            bg: '#FCFCFB', ink: NAVY },
  { id: 'paper',       name: 'Paper',       note: 'Pure white.',                                 bg: '#FFFFFF', ink: NAVY },
  { id: 'sand',        name: 'Sand',        note: 'Deeper warm paper. Premium-goods feel.',      bg: '#F2EAD9', ink: NAVY },
  { id: 'oat',         name: 'Oat',         note: 'Soft neutral beige. Calm, low-noise.',        bg: '#F4F0E8', ink: NAVY },
  { id: 'stone',       name: 'Stone',       note: 'Neutral grey paper. Architectural.',          bg: '#F1F1EF', ink: NAVY },
  { id: 'dove',        name: 'Dove',        note: 'Cool light grey. Quiet and minimal.',         bg: '#EEEFF1', ink: NAVY },
  { id: 'mist',        name: 'Mist',        note: 'Cool blue-grey wash.',                         bg: '#EDF0F4', ink: NAVY },
  { id: 'sky',         name: 'Sky',         note: 'Cool blue-white. Technical, trustworthy.',     bg: '#EEF3FA', ink: NAVY },
  { id: 'mint',        name: 'Mint',        note: 'Pale green wash. Fresh.',                       bg: '#EDF5EE', ink: NAVY },
  { id: 'seafoam',     name: 'Seafoam',     note: 'Pale aqua. Cool and clean.',                    bg: '#E9F4F1', ink: NAVY },
  { id: 'honeydew',    name: 'Honeydew',    note: 'Pale yellow-green. Fresh and soft.',            bg: '#F1F5E6', ink: NAVY },
  { id: 'butter',      name: 'Buttercream', note: 'Pale yellow. Sunny, optimistic.',               bg: '#FBF6E2', ink: NAVY },
  { id: 'peach',       name: 'Peach',       note: 'Warm peach. Friendly, consumer.',               bg: '#FBEEE4', ink: NAVY },
  { id: 'blush',       name: 'Blush',       note: 'Pale warm pink. Soft.',                          bg: '#FBEFEC', ink: NAVY },
  { id: 'rose',        name: 'Rose quartz', note: 'Cool pale pink.',                                bg: '#FAEEF1', ink: NAVY },
  { id: 'lavender',    name: 'Lavender',    note: 'Quiet violet-grey paper. Refined.',              bg: '#F1EFF8', ink: NAVY },
  { id: 'periwinkle',  name: 'Periwinkle',  note: 'Soft blue-violet.',                              bg: '#EEF0FB', ink: NAVY },
  { id: 'slate-paper', name: 'Slate paper', note: 'The old corporate default, for comparison.',    bg: '#F4F6F8', ink: '#16203A' },
]

export const ACCENTS: Accent[] = [
  { id: 'lime',       name: 'Lime',       hex: '#CDEB45' }, // brand
  { id: 'emerald',    name: 'Emerald',    hex: '#0E9F6E' },
  { id: 'forest',     name: 'Forest',     hex: '#2F7D4F' },
  { id: 'teal',       name: 'Teal',       hex: '#0E8B8B' },
  { id: 'cyan',       name: 'Cyan',       hex: '#0E86C4' },
  { id: 'cobalt',     name: 'Cobalt',     hex: '#2F5BFF' },
  { id: 'indigo',     name: 'Indigo',     hex: '#4F46E5' },
  { id: 'violet',     name: 'Violet',     hex: '#7C3AED' },
  { id: 'magenta',    name: 'Magenta',    hex: '#C026A6' },
  { id: 'pink',       name: 'Pink',       hex: '#DB2777' },
  { id: 'rose',       name: 'Rose',       hex: '#E11D48' },
  { id: 'orange',     name: 'Orange',     hex: '#EA580C' },
  { id: 'terracotta', name: 'Terracotta', hex: '#C1542A' },
  { id: 'amber',      name: 'Amber',      hex: '#B45309' },
  { id: 'navy',       name: 'Navy',       hex: '#16203A' },
]

export function getBackground(id: string): Background | undefined {
  return BACKGROUNDS.find((b) => b.id === id)
}
export function getAccent(id: string): Accent | undefined {
  return ACCENTS.find((a) => a.id === id)
}
